import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type RoleKey = "master" | "formerMaster" | "communityAmbassador" | "nameRater" | "mvp";

type PlayerItem = {
  id: string;
  name: string;
  friendCode: string | null;
  notes: string;
  roles: RoleKey[];
  level?: number | null; // <-- added
};

const PLAYERS_URL = "https://sopronpogo.blob.core.windows.net/pogo/players.json";

function CopyButton({ value }: { value: string | null }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!value) return;
    // remove all spaces from the friend code before copying
    const cleanValue = value.replace(/\s+/g, "");

    try {
      await navigator.clipboard.writeText(cleanValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = cleanValue;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const disabled = !value;
  return (
    <button
      type="button"
      className={`copy-btn${disabled ? " disabled" : ""}${copied ? " copied" : ""}`}
      onClick={copy}
      aria-label={copied ? t("common.copied", "Copied!") : t("common.copy", "Copy")}
      title={copied ? t("common.copied", "Copied!") : t("common.copy", "Copy")}
      disabled={disabled}
    >
      <svg className="copy-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path d="M16 4h-1V3a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v1H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h6v-2H6V6h1v1a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6h1v3h2V6a2 2 0 0 0-2-2Zm-3 0H9V4h4v0ZM20 10h-6a2 2 0 0 0-2 2v8h8a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2Zm0 8h-6v-6h6v6Z"/>
      </svg>
      <span className="copy-label">{copied ? t("common.copied", "Copied!") : t("common.copy", "Copy")}</span>
    </button>
  );
}


function LevelCircle({ level }: { level?: number | null }) {
  const { t } = useTranslation();
  const lvl = typeof level === "number" ? level : null;


  // color rules: <70 green, >70 orange, >=80 red
  const color = lvl === null ? "#9e9e9e" : (lvl >= 80 ? "#e53935" : (lvl >= 70 ? "#fb8c00" : "#2e7d32"));


  const style: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 9999,
  border: `3px solid ${color}`,
  background: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: 12,
  lineHeight: 1,
  color,
  boxSizing: "border-box",
  userSelect: "none",
  marginRight: 5
  };


  return (
  <div
  className="level-circle"
  style={style}
  title={lvl !== null ? t("playersPage.labels.levelN", { defaultValue: `Level ${lvl}`, level: lvl }) : t("playersPage.labels.levelNA", "Level N/A")}
  aria-label={lvl !== null ? t("playersPage.labels.levelN", { defaultValue: `Level ${lvl}`, level: lvl }) : t("playersPage.labels.levelNA", "Level N/A")}
  >
  {lvl ?? "-"}
  </div>
  );
}



export default function Players() {
  const { t, i18n } = useTranslation();
  const [players, setPlayers] = useState<PlayerItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const res = await fetch(PLAYERS_URL, { headers: { Accept: "application/json" }, cache: "no-cache" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as PlayerItem[];
        if (alive) setPlayers(Array.isArray(json) ? json : []);
      } catch (e: any) {
        if (alive) setErr(e?.message || "fetch_failed");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const list = useMemo(
    () => (players ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, i18n.language)),
    [players, i18n.language]
  );

const roleLabel = (r: RoleKey): string => {
  switch (r) {
    case "master":
    case "formerMaster":
      return t("playersPage.roles.master", "Master");
    case "mvp":
      return t(
        "playersPage.roles.MVP",
        "MVP👑"
      );
    case "communityAmbassador":
      return t(
        "playersPage.roles.communityAmbassador",
        "Community Ambassador"
      );

    default:
      return r; // fallback to the roleKey itself
  }
};

const roleAbbr = (r: RoleKey): string => {
  switch (r) {
    case "master":
    case "formerMaster":
      return "M";
    case "mvp":
      return "👑";
    case "communityAmbassador":
      return "CA";
    default:
      return "NR";
  }
};


  return (
    <div className="surface-body">
      <h1>{t("playersPage.title", "Players")} ({list.length})</h1>
      <p className="players-intro">{t("playersPage.intro", "List of players and friend codes.")}</p>

      {loading && <p style={{ opacity: .8 }}>{t("Loading...")}</p>}
      {err && <p className="error" role="alert">{t("Error")}: {err}</p>}

      {!loading && !err && (list.length === 0 ? (
        <p style={{ opacity: .8 }}>{t("playersPage.empty", "No players found.")}</p>
      ) : (
        <div className="players-table-wrap">
          <table className="players-table" role="table" aria-label={t("playersPage.tableLabel", "Players table")}>
            <thead>
              <tr>
                <th>{t("playersPage.columns.name", "Name")}</th>
                <th></th>{/* badges column */}
                <th className="friendcode-col">
                  <span className="hide-mobile">{t("playersPage.columns.friendCode", "Friend code")}</span>
                </th>
                <th className="hide-mobile">{t("playersPage.columns.notes", "Notes")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p, idx) => (
                <tr key={p.id} className={idx % 2 ? "alt-row" : ""}>
                  <td className="player-name">
                    <LevelCircle level={p.level} />
                    {p.name}
                  </td>
                  <td className="player-roles">
                    {p.roles?.length ? (
                      <div className="inline-badges">
                        {p.roles.map((r) => (
                          <span
                            key={r}
                            className={`role-badge role-${r}`}
                            title={roleLabel(r)}
                            aria-label={roleLabel(r)}
                          >
                            <span className="role-full">{roleLabel(r)}</span>
                            <span className="role-abbr">{roleAbbr(r)}</span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td className="player-friendcode">
                    <span className={`friend-text ${p.friendCode ? "" : "muted"}`}>
                      {p.friendCode ?? t("playersPage.labels.n/a", "N/A")}
                    </span>
                    <CopyButton value={p.friendCode} />
                  </td>
                  <td className="player-notes-cell hide-mobile">
                    <div className="notes-text">{p.notes}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
